import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageSource } from '@vedalib/editor/lib/files'
import type React from 'react'
import { useCallback } from 'react'
import type { FileTypeEnum } from 'utils'

import { useFilePickerModal } from 'components/pages/FinderPage'

import type { FileTypes } from './FileLoader'

type IFieldFile<V extends FileUsage | null> = {
  value?: V
  fileType: FileTypes
  onBlur?: (e?: React.FocusEvent) => void
  onFocus?: (e?: React.FocusEvent) => void
  onChange?: (value: V) => void
}

export const useFileHandlers = <V extends FileUsage | null>({
  onChange,
  value,
  onFocus,
  onBlur,
  fileType,
}: IFieldFile<V>) => {
  const filePick = useFilePickerModal({
    defaultContentType: (fileType === 'document' ? undefined : fileType) as FileTypeEnum,
  })

  const handlePickFile = async () => {
    onFocus?.()
    const fileId = await filePick.open({})
    if (fileId) {
      onChange?.({ ...(value as V), id: fileId, source: FileUsageSource.file, params: undefined })
    }
    onBlur?.()
  }

  const handleResetFile = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (value) {
      onChange?.({ ...value, id: null, source: FileUsageSource.file, params: undefined })
    }
  }

  const handleChangeUrl = useCallback(
    (url: string) => onChange?.({ ...(value as V), source: FileUsageSource.url, url }),
    [onChange, value],
  )

  const handleChangeObjectFit = useCallback(
    (objectFit: string) => onChange?.({ ...(value as V), objectFit }),
    [onChange, value],
  )

  return {
    handlePickFile,
    handleResetFile,
    handleChangeUrl,
    handleChangeObjectFit,
  }
}
